import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Save } from "lucide-react";
import { Button, Card, Input, Textarea } from "@samklang/ui";
import type { Perspective } from "@samklang/schemas";
import { useKartlaggningStore } from "@/stores/kartlaggning-store";
import { formatDate, perspectiveLabel, perspectiveShortLabel } from "./lib";

interface Props {
  readonly kartlaggningId: string;
}

const FOLLOW_UP_QUESTION: Readonly<Record<Perspective, string>> = {
  elev: "Har det blivit lättare för dig?",
  mentor: "Har anpassningen fungerat?",
  vardnadshavare: "Har ni märkt en förändring?",
  annan: "Vilken förändring ser du sedan insatsen infördes?",
};

export function KartlaggningFollowUpScreen({ kartlaggningId }: Props) {
  const navigate = useNavigate();
  const kartlaggning = useKartlaggningStore((store) => store.getKartlaggning(kartlaggningId));
  const addFollowUp = useKartlaggningStore((store) => store.addFollowUp);

  const [values, setValues] = useState<Readonly<Record<string, { value: number; comment: string; conductedBy: string }>>>({});
  const [saved, setSaved] = useState(false);

  if (!kartlaggning) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <Card><h1 className="font-display text-2xl font-semibold">Kartläggningen finns inte.</h1></Card>
      </section>
    );
  }

  const setValue = (perspective: Perspective, patch: Partial<{ value: number; comment: string; conductedBy: string }>) => {
    setValues((current) => ({
      ...current,
      [perspective]: { ...valueFor(current, perspective), ...patch },
    }));
    setSaved(false);
  };

  const save = (perspective: Perspective) => {
    const entry = values[perspective];
    if (!entry || entry.value < 1) return;
    const run = kartlaggning.runs.find((candidate) => candidate.perspective === perspective);
    const chosenRole = run?.role;
    addFollowUp(kartlaggningId, {
      perspective,
      ...(chosenRole ? { role: chosenRole } : {}),
      value: entry.value,
      comment: entry.comment.trim(),
      conductedBy: entry.conductedBy.trim() || run?.conductedBy.trim() || kartlaggning.createdBy,
    });
    setSaved(true);
  };

  return (
    <section className="mx-auto w-full max-w-3xl px-5 py-8 sm:px-8">
      <button onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId } })} className="flex items-center gap-1 text-sm font-semibold text-primary">
        <ArrowLeft size={16} /> Till kartläggningen
      </button>
      <h1 className="mt-3 font-display text-4xl font-semibold">Följ upp</h1>
      <p className="mt-2 text-foreground-muted">Hur upplever varje perspektiv situationen nu, sedan insatser infördes?</p>

      {saved ? <Card className="mt-5"><p className="text-sm text-secondary">Uppföljningen är sparad i kartläggningen.</p></Card> : null}

      <div className="mt-6 grid gap-4">
        {kartlaggning.runs.map((run) => {
          const entry = valueFor(values, run.perspective);
          return (
            <Card key={run.perspective} padding="md">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-display text-xl font-semibold">{perspectiveLabel(run.perspective, run.role)}</h2>
                <span className="rounded-full bg-surface-muted px-3 py-1 text-sm text-foreground-muted">
                  {kartlaggning.followUps.filter((f) => f.perspective === run.perspective).length} uppföljning(ar)
                </span>
              </div>
              <p className="mt-2 text-lg">{FOLLOW_UP_QUESTION[run.perspective]}</p>

              <fieldset className="mt-4">
                <legend className="text-xs font-semibold uppercase tracking-wider text-primary">Skala 1–5</legend>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((step) => (
                    <button
                      key={step}
                      type="button"
                      onClick={() => setValue(run.perspective, { value: step })}
                      aria-pressed={entry.value === step}
                      className={`flex h-12 min-w-12 items-center justify-center rounded-full border px-3 text-lg font-semibold ${entry.value === step ? "border-primary bg-primary text-primary-foreground" : "border-border bg-surface-raised text-foreground"}`}
                    >
                      {step}
                    </button>
                  ))}
                </div>
              </fieldset>

              <label className="mt-4 grid gap-1 font-semibold">
                Kommentar (frivilligt)
                <Textarea value={entry.comment} onChange={(event) => setValue(run.perspective, { comment: event.target.value })} maxLength={1000} placeholder="Skriv om du vill" />
              </label>
              <label className="mt-4 grid gap-1 font-semibold">
                Genomfört av
                <Input value={entry.conductedBy} onChange={(event) => setValue(run.perspective, { conductedBy: event.target.value })} maxLength={80} placeholder={run.conductedBy || "Vem genomför uppföljningen?"} />
              </label>

              <Button className="mt-4" onClick={() => save(run.perspective)} disabled={entry.value < 1}>
                <Save size={18} /> Spara uppföljning
              </Button>
            </Card>
          );
        })}
      </div>

      {kartlaggning.followUps.length > 0 ? (
        <Card className="mt-6">
          <h2 className="font-display text-lg font-semibold">Tidigare uppföljningar</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {[...kartlaggning.followUps].reverse().map((followUp) => (
              <li key={followUp.id} className="rounded-2xl bg-surface-muted p-3">
                <strong>{perspectiveShortLabel(followUp.perspective, followUp.role)}</strong> {followUp.value}/5 · {formatDate(followUp.at)} · {followUp.conductedBy}
                {followUp.comment ? <span className="block text-foreground-muted">— {followUp.comment}</span> : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </section>
  );
}

function valueFor(values: Readonly<Record<string, { value: number; comment: string; conductedBy: string }>>, perspective: Perspective): { value: number; comment: string; conductedBy: string } {
  return values[perspective] ?? { value: 0, comment: "", conductedBy: "" };
}
